import { Check, ContentCopy, PlayDisabled } from '@mui/icons-material';
import { Divider, ListItemIcon, ListItemText, MenuItem, Menu as MuiMenu } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useDeleteDeviceSignal } from '~/api/device-signals/delete';
import { useDuplicateDeviceSignal } from '~/api/device-signals/duplicate';
import { useUpdateDeviceSignal } from '~/api/device-signals/update';
import { ActionButton } from '~/components/button';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import {
  MoreDeleteAction,
  mergeGridActionProps,
  type GridActionCellProps,
} from '~/components/table';
import { useConfirmDialog } from '~/hooks/dialogs';
import { useLink } from '~/hooks/link';
import { useMoreActions } from '~/hooks/table';
import { assert } from '~/lib/assert';
import type { NetworkDeviceSignals__DeviceSignal as DeviceSignal } from '../../queries/list.generated';

export interface MenuProps extends GridActionCellProps {
  deviceSignal: DeviceSignal;
}

export const Menu = ({ deviceSignal, ...cellProps }: MenuProps) => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const link = useLink();

  const [moreMenuProps, moreActionProps, moreTableActionProps] = useMoreActions<DeviceSignal>();
  const deviceSignalActionProps = mergeGridActionProps(
    moreTableActionProps(deviceSignal),
    cellProps,
  );

  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();

  const [deleteSignal] = useDeleteDeviceSignal();
  const [duplicateSignal] = useDuplicateDeviceSignal();
  const [updateSignal] = useUpdateDeviceSignal();

  return (
    <>
      <ActionButton {...deviceSignalActionProps} />
      <MuiMenu {...moreMenuProps}>
        {deviceSignal.enabled ? (
          <MenuItem
            onClick={async () => {
              assert(moreMenuProps.context != null, 'signal: no context');
              await updateSignal({
                variables: {
                  id: moreMenuProps.context.id,
                  patch: { enabled: false },
                },
              });
              moreMenuProps.onClose();
            }}
          >
            <ListItemIcon>
              <PlayDisabled />
            </ListItemIcon>
            <ListItemText>{t('pages.device_signals.list.menu.disable')}</ListItemText>
          </MenuItem>
        ) : (
          <MenuItem
            onClick={async () => {
              assert(moreMenuProps.context != null, 'signal: no context');
              await updateSignal({
                variables: { id: moreMenuProps.context.id, patch: { enabled: true } },
              });
              moreMenuProps.onClose();
            }}
          >
            <ListItemIcon>
              <Check />
            </ListItemIcon>
            <ListItemText>{t('pages.device_signals.list.menu.enable')}</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          onClick={async () => {
            assert(moreMenuProps.context != null, 'signal: no context');
            const sourceId = moreMenuProps.context.id;
            moreMenuProps.onClose();
            const { data } = await duplicateSignal({
              variables: { input: { deviceSignalId: sourceId } },
            });
            const deviceSignalId = data?.duplicateDeviceSignal?.deviceSignal.id;
            if (!deviceSignalId) return;
            navigate(link(`/device-signals/${deviceSignalId}`));
          }}
        >
          <ListItemIcon>
            <ContentCopy />
          </ListItemIcon>
          <ListItemText>{t('pages.device_signals.list.menu.duplicate')}</ListItemText>
        </MenuItem>
        <Divider />
        <MoreDeleteAction
          {...moreActionProps}
          disabled={!moreMenuProps.context?.canDestroy.value}
          tooltip={
            moreMenuProps.context?.canDestroy.value
              ? t('pages.device_signals.list.menu.delete_tooltip')
              : moreMenuProps.context?.canDestroy.reasons?.fullMessages.join(', ') ||
                t('pages.device_signals.list.menu.delete_disabled_tooltip')
          }
          onClick={async () => {
            assert(moreMenuProps.context !== undefined, 'Data missing from context');
            if (!(await confirmDelete())) return;
            await deleteSignal({
              variables: {
                input: { signalIds: [moreMenuProps.context.id] },
              },
            });
          }}
        />
      </MuiMenu>

      <ConfirmDialog
        {...confirmDeleteProps}
        confirm={t('common.dialogs.permanently_delete')}
        deleteConfirm
        prompt={t('pages.device_signals.list.dialogs.delete_one.prompt')}
        title={t('pages.device_signals.list.dialogs.delete_one.title')}
      />
    </>
  );
};
